<script setup>
import { computed, watch, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
const router = useRouter();
const store = useStore();
const { isWatchdogBlue, pushPageParamToUrl, alignment } = defineProps({
  isWatchdogBlue: {
    type: Boolean,
    required: false,
    default: false,
  },
  pushPageParamToUrl: {
    type: Boolean,
    required: false,
    default: false,
  },
  alignment: {
    type: String,
    required: false,
    default: 'center',
  },
});
const activePageTab = computed(
  () => router.currentRoute.value.query.tab || 'unassigned'
);
const currentPage = computed(
  () =>
    store.state.myLoadsStore[activePageTab.value].pagination.currentPage || 1
);
const totalPages = computed(
  () => store.state.myLoadsStore[activePageTab.value].pagination.totalPages || 1
);
const emit = defineEmits(['selectPage']);
const counterPageAround = computed(() =>
  totalPages.value > 5 ? 2 : totalPages.value
);
// const range = ref({
//   low: 1,
//   high: 5,
// });
const shownPages = ref([]);
const range = computed(() => {
  let low = Math.max(1, currentPage.value - counterPageAround.value);
  let high = Math.min(
    currentPage.value + counterPageAround.value,
    totalPages.value
  );
  // if currentPage < 3, show first 5 pages
  if (totalPages.value > 5 && currentPage.value < 3) {
    low = 1;
    high = 5;
  }
  // if currentPage > totalPages - 2, show last 5 pages
  if (totalPages.value > 5 && currentPage.value > totalPages.value - 2) {
    low = totalPages.value - 4;
    high = totalPages.value;
  }
  return { low, high };
});
watch(
  () => range.value,
  (newVal) => {
    const numbers = [];
    for (let i = newVal.low; i <= newVal.high; i++) {
      numbers.push(i);
    }
    shownPages.value = numbers;
  },
  { deep: true, immediate: true }
);
const handleShowMoreClick = (direction = 'right') => {
  if (direction === 'right') {
    if (shownPages.value[shownPages.value.length - 1] + 5 <= totalPages.value) {
      shownPages.value = shownPages.value.map((page) => page + 5);
    } else {
      shownPages.value = shownPages.value.map(
        (page) =>
          page +
          (totalPages.value - shownPages.value[shownPages.value.length - 1])
      );
    }
  } else {
    if (shownPages.value[0] - 5 >= 1) {
      shownPages.value = shownPages.value.map((page) => page - 5);
    } else {
      shownPages.value = shownPages.value.map((page) => page - 1);
    }
  }
  // shownPages.value = Math.min(range.value.high + 5, totalPages);
};
// const handleScrollClick = () => {
//   document.body.scrollTop = 0; // For Safari
//   document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
// };
const handlePageClick = (page) => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  if (pushPageParamToUrl) {
    router.push({
      query: {
        ...router.currentRoute.value.query,
        page: page,
      },
    });
    return;
  }
  emit('selectPage', page);
};
</script>

<template>
  <div class="flex flex-col items-center justify-center">
    <div
      class="flex w-full items-center px-2"
      :class="{
        'justify-center': alignment === 'center',
      }"
    >
      <div class="my-4 flex items-center gap-1 text-sm">
        <button
          :title="`Go to page 1`"
          class="flex h-10 w-10 items-center justify-center rounded border-light-gray px-1 py-2"
          :class="{ 'hover:bg-placeholder-gray': currentPage !== 1 }"
          :disabled="currentPage === 1"
          @click="handlePageClick(1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.1414 17.8486C10.3394 18.0466 10.4384 18.1456 10.5526 18.1827C10.653 18.2153 10.7612 18.2153 10.8616 18.1827C10.9758 18.1456 11.0748 18.0466 11.2728 17.8486L11.5556 17.5657C11.7536 17.3677 11.8526 17.2687 11.8897 17.1545C11.9224 17.0541 11.9224 16.9459 11.8897 16.8455C11.8526 16.7314 11.7536 16.6324 11.5556 16.4343L7.39411 12.2728C7.1961 12.0748 7.0971 11.9758 7.06001 11.8616C7.02738 11.7612 7.02738 11.6531 7.06001 11.5526C7.0971 11.4385 7.1961 11.3395 7.39411 11.1415L11.5556 6.97993C11.7536 6.78192 11.8526 6.68292 11.8897 6.56875C11.9224 6.46833 11.9224 6.36016 11.8897 6.25974C11.8526 6.14557 11.7536 6.04657 11.5556 5.84856L11.2728 5.56572C11.0748 5.36771 10.9758 5.26871 10.8616 5.23161C10.7612 5.19898 10.653 5.19898 10.5526 5.23161C10.4384 5.26871 10.3394 5.36771 10.1414 5.56572L4.56569 11.1415C4.36768 11.3395 4.26867 11.4385 4.23158 11.5526C4.19895 11.6531 4.19895 11.7612 4.23158 11.8616C4.26867 11.9758 4.36768 12.0748 4.56568 12.2728L10.1414 17.8486Z"
              fill="#6C7C9D"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M17.4344 5.56569C17.6324 5.36768 17.7314 5.26867 17.8456 5.23158C17.946 5.19895 18.0542 5.19895 18.1546 5.23158C18.2687 5.26867 18.3678 5.36768 18.5658 5.56569L18.8486 5.84853C19.0466 6.04654 19.1456 6.14554 19.1827 6.2597C19.2153 6.36013 19.2153 6.4683 19.1827 6.56872C19.1456 6.68289 19.0466 6.78189 18.8486 6.9799L14.6871 11.1414C14.4891 11.3394 14.3901 11.4384 14.353 11.5526C14.3203 11.653 14.3203 11.7612 14.353 11.8616C14.3901 11.9758 14.4891 12.0748 14.6871 12.2728L18.8486 16.4343C19.0466 16.6323 19.1456 16.7313 19.1827 16.8455C19.2153 16.9459 19.2153 17.0541 19.1827 17.1545C19.1456 17.2687 19.0466 17.3677 18.8486 17.5657L18.5658 17.8485C18.3678 18.0465 18.2687 18.1455 18.1546 18.1826C18.0542 18.2153 17.946 18.2153 17.8456 18.1826C17.7314 18.1455 17.6324 18.0465 17.4344 17.8485L11.8587 12.2728C11.6606 12.0748 11.5616 11.9758 11.5245 11.8616C11.4919 11.7612 11.4919 11.653 11.5245 11.5526C11.5616 11.4384 11.6606 11.3394 11.8587 11.1414L17.4344 5.56569Z"
              fill="#6C7C9D"
            />
          </svg>
        </button>
        <button
          :title="`Go to page ${currentPage - 1}`"
          class="flex h-10 w-10 items-center justify-center rounded-[5px] border-light-gray px-1 py-2"
          :class="{ 'hover:bg-placeholder-gray': currentPage !== 1 }"
          :disabled="currentPage === 1"
          @click="handlePageClick(currentPage - 1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M13.7274 5.85853C13.9254 5.66052 14.0244 5.56152 14.1385 5.52443C14.239 5.4918 14.3471 5.4918 14.4476 5.52443C14.5617 5.56152 14.6607 5.66052 14.8587 5.85853L15.1416 6.14137C15.3396 6.33938 15.4386 6.43839 15.4757 6.55255C15.5083 6.65297 15.5083 6.76115 15.4757 6.86157C15.4386 6.97573 15.3396 7.07474 15.1416 7.27275L10.98 11.4343C10.782 11.6323 10.683 11.7313 10.6459 11.8454C10.6133 11.9459 10.6133 12.054 10.6459 12.1545C10.683 12.2686 10.782 12.3676 10.9801 12.5656L15.1416 16.7272C15.3396 16.9252 15.4386 17.0242 15.4757 17.1383C15.5083 17.2388 15.5083 17.3469 15.4757 17.4474C15.4386 17.5615 15.3396 17.6605 15.1416 17.8585L14.8587 18.1414C14.6607 18.3394 14.5617 18.4384 14.4476 18.4755C14.3471 18.5081 14.239 18.5081 14.1385 18.4755C14.0244 18.4384 13.9254 18.3394 13.7274 18.1414L8.15162 12.5656C7.95361 12.3676 7.85461 12.2686 7.81752 12.1545C7.78489 12.054 7.78489 11.9459 7.81752 11.8454C7.85461 11.7313 7.95361 11.6323 8.15162 11.4343L13.7274 5.85853Z"
              fill="#6C7C9D"
            />
          </svg>
        </button>
        <button
          v-if="shownPages[0] !== 1"
          :title="`Go to page 1`"
          class="text-info-bold-text flex h-10 w-10 items-center justify-center rounded border-light-gray px-3 py-2 text-sm"
          :class="[
            {
              'wd-blue-bg': 1 === currentPage && isWatchdogBlue,
              'bg-yellow-500': 1 === currentPage && !isWatchdogBlue,
              'hover:border-yellow-400': 1 !== currentPage && !isWatchdogBlue,
            },
            1 !== currentPage
              ? 'cursor-pointer hover:bg-placeholder-gray'
              : 'cursor-default border-transparent text-white',
          ]"
          @click="handlePageClick(1)"
        >
          1
        </button>
        <button
          v-if="shownPages[0] !== 1"
          :title="`See previous ${Math.min(shownPages[0] - 1, 5)} pages`"
          class="text-info-bold-text flex h-10 w-10 justify-center rounded border-light-gray px-3 py-2 text-sm hover:bg-placeholder-gray"
          @click="handleShowMoreClick('left')"
        >
          ...
        </button>
        <button
          v-for="page in shownPages"
          :key="'page_' + page"
          :title="`Go to page ${page}`"
          class="flex h-10 w-10 items-center justify-center rounded-[5px] p-1 text-sm text-steel-1100"
          :disabled="page === currentPage"
          :class="[
            {
              'wd-blue-bg': page === currentPage && isWatchdogBlue,
              'bg-sun-50 outline outline-1 outline-offset-[-1px] outline-sun-400':
                page === currentPage && !isWatchdogBlue,
              'hover:border-yellow-400':
                page !== currentPage && !isWatchdogBlue,
            },
            page !== currentPage
              ? 'cursor-pointer hover:bg-placeholder-gray'
              : 'cursor-default border-transparent',
          ]"
          @click="handlePageClick(page)"
        >
          {{ page }}
        </button>
        <button
          v-if="shownPages[shownPages.length - 1] !== totalPages"
          :title="`See next ${Math.min(totalPages - shownPages[shownPages.length - 1] - 1, 5)} pages`"
          class="text-info-bold-text flex h-10 w-10 items-center justify-center rounded border-light-gray px-3 py-2 text-sm hover:bg-placeholder-gray"
          :disabled="shownPages[shownPages.length - 1] === totalPages"
          @click="handleShowMoreClick('right')"
        >
          ...
        </button>
        <button
          v-if="shownPages[shownPages.length - 1] !== totalPages"
          :title="`Go to page ${totalPages}`"
          class="text-info-bold-text flex h-10 w-10 items-center justify-center rounded border-light-gray px-3 py-2 text-sm hover:bg-placeholder-gray"
          :disabled="totalPages === currentPage"
          :class="[
            {
              'wd-blue-bg': totalPages === currentPage && isWatchdogBlue,
              'bg-yellow-500': totalPages === currentPage && !isWatchdogBlue,
              'hover:border-yellow-400':
                totalPages !== currentPage && !isWatchdogBlue,
            },
            totalPages !== currentPage
              ? 'cursor-pointer text-body-color'
              : 'cursor-default border-transparent text-white',
          ]"
          @click="handlePageClick(totalPages)"
        >
          {{ totalPages }}
        </button>
        <button
          :title="`Go to page ${currentPage + 1}`"
          class="flex h-10 w-10 items-center justify-center rounded border-light-gray px-1 py-2"
          :class="{ 'hover:bg-placeholder-gray': currentPage !== totalPages }"
          :disabled="currentPage === totalPages"
          @click="handlePageClick(currentPage + 1)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.2729 18.1414C10.0749 18.3394 9.97585 18.4384 9.86169 18.4755C9.76127 18.5081 9.6531 18.5081 9.55267 18.4755C9.43851 18.4384 9.33951 18.3394 9.1415 18.1414L8.85865 17.8585C8.66065 17.6605 8.56164 17.5615 8.52455 17.4474C8.49192 17.3469 8.49192 17.2388 8.52455 17.1383C8.56164 17.0242 8.66065 16.9252 8.85865 16.7272L13.0202 12.5656C13.2182 12.3676 13.3172 12.2686 13.3543 12.1545C13.3869 12.054 13.3869 11.9459 13.3543 11.8454C13.3172 11.7313 13.2182 11.6323 13.0202 11.4343L8.85865 7.27275C8.66065 7.07474 8.56164 6.97573 8.52455 6.86157C8.49192 6.76115 8.49192 6.65297 8.52455 6.55255C8.56164 6.43839 8.66065 6.33938 8.85865 6.14137L9.1415 5.85853C9.33951 5.66052 9.43851 5.56152 9.55267 5.52443C9.6531 5.4918 9.76127 5.4918 9.86169 5.52443C9.97585 5.56152 10.0749 5.66052 10.2729 5.85853L15.8486 11.4343C16.0466 11.6323 16.1456 11.7313 16.1827 11.8454C16.2153 11.9459 16.2153 12.054 16.1827 12.1545C16.1456 12.2686 16.0466 12.3676 15.8486 12.5656L10.2729 18.1414Z"
              fill="#6C7C9D"
            />
          </svg>
        </button>
        <button
          :title="`Go to page ${totalPages}`"
          class="flex h-10 w-10 items-center justify-center rounded border-light-gray px-1 py-2"
          :class="{ 'hover:bg-placeholder-gray': currentPage !== totalPages }"
          :disabled="currentPage === totalPages"
          @click="handlePageClick(totalPages)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="14"
            viewBox="0 0 16 14"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M9.27264 12.8486C9.07463 13.0466 8.97563 13.1456 8.86146 13.1827C8.76104 13.2153 8.65287 13.2153 8.55245 13.1827C8.43828 13.1456 8.33928 13.0466 8.14127 12.8486L7.85843 12.5657C7.66042 12.3677 7.56142 12.2687 7.52432 12.1545C7.49169 12.0541 7.49169 11.9459 7.52432 11.8455C7.56142 11.7314 7.66042 11.6324 7.85843 11.4343L12.02 7.27282C12.218 7.07482 12.317 6.97581 12.3541 6.86165C12.3867 6.76123 12.3867 6.65305 12.3541 6.55263C12.317 6.43847 12.218 6.33946 12.02 6.14145L7.85843 1.97993C7.66042 1.78192 7.56142 1.68292 7.52432 1.56875C7.49169 1.46833 7.49169 1.36016 7.52432 1.25974C7.56142 1.14557 7.66042 1.04657 7.85843 0.848561L8.14127 0.565718C8.33928 0.36771 8.43828 0.268706 8.55245 0.231612C8.65287 0.198983 8.76104 0.198983 8.86147 0.231612C8.97563 0.268706 9.07463 0.36771 9.27264 0.565717L14.8484 6.14145C15.0464 6.33946 15.1454 6.43847 15.1825 6.55263C15.2151 6.65305 15.2151 6.76123 15.1825 6.86165C15.1454 6.97581 15.0464 7.07482 14.8484 7.27282L9.27264 12.8486Z"
              fill="#6C7C9D"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.97967 0.565685C1.78166 0.367677 1.68266 0.268673 1.5685 0.231578C1.46807 0.198949 1.3599 0.198949 1.25948 0.231578C1.14531 0.268673 1.04631 0.367677 0.848302 0.565685L0.56546 0.848528C0.367451 1.04654 0.268447 1.14554 0.231353 1.2597C0.198724 1.36013 0.198724 1.4683 0.231352 1.56872C0.268447 1.68289 0.367451 1.78189 0.56546 1.9799L4.72698 6.14142C4.92499 6.33943 5.02399 6.43843 5.06109 6.5526C5.09372 6.65302 5.09372 6.76119 5.06109 6.86161C5.02399 6.97578 4.92499 7.07478 4.72698 7.27279L0.56546 11.4343C0.367451 11.6323 0.268447 11.7313 0.231352 11.8455C0.198724 11.9459 0.198724 12.0541 0.231353 12.1545C0.268447 12.2687 0.367451 12.3677 0.56546 12.5657L0.848302 12.8485C1.04631 13.0465 1.14531 13.1455 1.25948 13.1826C1.3599 13.2153 1.46807 13.2153 1.5685 13.1826C1.68266 13.1455 1.78166 13.0465 1.97967 12.8485L7.55541 7.27279C7.75342 7.07478 7.85242 6.97578 7.88952 6.86161C7.92214 6.76119 7.92214 6.65302 7.88952 6.5526C7.85242 6.43843 7.75342 6.33943 7.55541 6.14142L1.97967 0.565685Z"
              fill="#6C7C9D"
            />
          </svg>
        </button>
      </div>
    </div>
    <!-- <button
      v-if="totalPages > 1"
      @click="handleScrollClick"
      class="text flex items-center justify-center rounded border border-transparent px-3 py-2 text-body-color"
    >
      Scroll to Top
    </button> -->
  </div>
</template>
<style scoped>
.wd-blue-bg {
  background-color: rgb(23, 44, 92);
}
</style>
